import {
  ActionBarPrimitive,
  MessagePrimitive,
  useAuiState,
  type ReasoningMessagePartProps,
  type TextMessagePartProps,
  type ToolCallMessagePartProps,
} from '@assistant-ui/react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CopyIcon from '@mui/icons-material/ContentCopy';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import 'highlight.js/styles/github.css';

// XSS-safe sanitize schema — SEC-04 compliance (preserved from Phase 13)
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [...(defaultSchema.attributes?.code ?? []), ['className', /^language-/]],
  },
};

// MUI-styled markdown component overrides (per UI-SPEC Markdown Rendering Contract)
const muiMarkdownComponents = {
  p: ({ children }: { children?: React.ReactNode }) => (
    <Typography variant="body1" sx={{ mb: 1 }}>
      {children}
    </Typography>
  ),
  code: ({ className, children }: { className?: string; children?: React.ReactNode }) => {
    if (className) return <code className={className}>{children}</code>;
    return (
      <Box
        component="code"
        sx={{
          fontFamily: 'monospace',
          bgcolor: 'grey.100',
          px: 0.5,
          borderRadius: 0.5,
          fontSize: '0.875rem',
        }}
      >
        {children}
      </Box>
    );
  },
  pre: ({ children }: { children?: React.ReactNode }) => (
    <Box component="pre" sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1, overflow: 'auto' }}>
      {children}
    </Box>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  h1: ({ children }: { children?: React.ReactNode }) => (
    <Typography variant="h6">{children}</Typography>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <Typography variant="h6">{children}</Typography>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <Typography variant="h6">{children}</Typography>
  ),
  h4: ({ children }: { children?: React.ReactNode }) => (
    <Typography variant="h6">{children}</Typography>
  ),
};

// TextPart: renders markdown with XSS sanitization (UICM-06)
const TextPart = ({ text, status }: TextMessagePartProps) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, sanitizeSchema], rehypeHighlight]}
        components={muiMarkdownComponents}
      >
        {text}
      </ReactMarkdown>
    </Box>
    {status.type === 'running' && <CircularProgress size={16} sx={{ mt: 0.5 }} />}
  </Box>
);

// ToolCallPart: MUI Accordion with status indicators (D-06)
const ToolCallPart = ({ toolName, argsText, result, status }: ToolCallMessagePartProps) => {
  const icon =
    status.type === 'running' ? (
      <CircularProgress size={16} />
    ) : status.type === 'incomplete' ? (
      <ErrorOutlineIcon color="error" fontSize="small" />
    ) : (
      <CheckCircleOutlineIcon color="success" fontSize="small" />
    );

  let displayContent: string;
  try {
    displayContent = JSON.stringify(result ?? JSON.parse(argsText), null, 2);
  } catch {
    displayContent = argsText;
  }

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ bgcolor: 'grey.50', '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {icon}
        <Typography variant="subtitle1" sx={{ ml: 1 }}>
          {toolName}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto', m: 0 }}>
          {displayContent}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

// ReasoningPart: debug-only accordion with auto-expand during streaming (D-05, UICM-05)
const ReasoningPart = ({ text, status }: ReasoningMessagePartProps) => {
  const isDebug = new URLSearchParams(window.location.search).get('debug') === 'true';
  if (!isDebug) return null;

  const isRunning = status.type === 'running';

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={isRunning ? true : undefined}
      sx={{
        bgcolor: 'grey.50',
        borderLeft: 2,
        borderColor: 'grey.400',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="caption" color="text.secondary">
          Reasoning
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
          {text}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
};

// UserActionBar: edit button on user messages (D-14: any user message)
const UserActionBar = () => {
  const { t } = useTranslation();
  return (
    <ActionBarPrimitive.Root>
      <Box
        className="msg-actions"
        sx={{ opacity: 0, transition: 'opacity 200ms', display: 'flex', gap: 0.5 }}
      >
        <ActionBarPrimitive.Copy asChild>
          <IconButton size="small" aria-label={t('aiChat.message.edit')}>
            <CopyIcon fontSize="inherit" />
          </IconButton>
        </ActionBarPrimitive.Copy>
      </Box>
    </ActionBarPrimitive.Root>
  );
};

// AssistantActionBar: regenerate button on last assistant message (D-15)
// ActionBarPrimitive.Reload auto-hides on non-last messages
const AssistantActionBar = () => {
  const { t } = useTranslation();
  return (
    <ActionBarPrimitive.Root>
      <Box
        className="msg-actions"
        sx={{ opacity: 0, transition: 'opacity 200ms', display: 'flex', gap: 0.5 }}
      >
        <ActionBarPrimitive.Reload asChild>
          <IconButton size="small" aria-label={t('aiChat.message.regenerate')}>
            <RefreshIcon fontSize="inherit" />
          </IconButton>
        </ActionBarPrimitive.Reload>
        <ActionBarPrimitive.Copy asChild>
          <IconButton size="small" aria-label={t('aiChat.message.copy', 'copy')}>
            <CopyIcon fontSize="inherit" />
          </IconButton>
        </ActionBarPrimitive.Copy>
      </Box>
    </ActionBarPrimitive.Root>
  );
};

// MessageRow: renders user and assistant messages with role icons, bubble styling,
// timestamps, markdown, tool accordions, and reasoning (UICM-01)
export const MessageRow = () => {
  const message = useAuiState((s) => s.message);
  const isUser = message.role === 'user';

  const timestamp = formatDistanceToNow(new Date(message.createdAt), { addSuffix: true });

  if (isUser) {
    return (
      <Box display="flex" justifyContent="flex-end" mb={1} gap={1}>
        <Box
          sx={{
            position: 'relative',
            maxWidth: '80%',
            '&:hover .msg-actions': { opacity: 1 },
            '&:hover .msg-timestamp': { opacity: 1 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              bgcolor: 'grey.100',
              borderRadius: 2,
              px: 2,
              py: 1,
            }}
          >
            <MessagePrimitive.Parts
              components={{
                Text: TextPart,
              }}
            />
          </Paper>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <UserActionBar />
          </Box>
          {timestamp && (
            <Typography
              variant="caption"
              color="text.secondary"
              className="msg-timestamp"
              sx={{
                display: 'block',
                textAlign: 'right',
                mt: 0.25,
                opacity: 0,
                transition: 'opacity 200ms',
              }}
            >
              {timestamp}
            </Typography>
          )}
        </Box>
        <PersonIcon
          fontSize="small"
          sx={{ color: 'text.secondary', alignSelf: 'flex-start', mt: 0.5 }}
        />
      </Box>
    );
  }

  // Assistant message
  return (
    <Box display="flex" justifyContent="flex-start" mb={1} gap={1}>
      <SmartToyIcon
        fontSize="small"
        sx={{ color: 'text.secondary', alignSelf: 'flex-start', mt: 0.5 }}
      />
      <Box
        sx={{
          maxWidth: '80%',
          position: 'relative',
          '&:hover .msg-actions': { opacity: 1 },
          '&:hover .msg-timestamp': { opacity: 1 },
        }}
      >
        <MessagePrimitive.Parts
          components={{
            Text: TextPart,
            Reasoning: ReasoningPart,
            tools: { Fallback: ToolCallPart },
          }}
        />
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <AssistantActionBar />
        </Box>
        {timestamp && (
          <Typography
            variant="caption"
            color="text.secondary"
            className="msg-timestamp"
            sx={{
              display: 'block',
              mt: 0.25,
              opacity: 0,
              transition: 'opacity 200ms',
            }}
          >
            {timestamp}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MessageRow;
